import React, { ChangeEvent } from "react";
import * as R from "ramda";
import { AccountFormsField } from "./utils";
import { AliasTag, EmailAddressTag } from "../types";
import { api } from "../../../dependencies";
import { IUserAccount } from "../../types/common";
import { ElementState, UserAccountPatchResponse } from "../types";
import { getCurrentAliases } from "../../../api/UsersApi/helpers";

export interface BasicInformationProps {
  accountAlias: ElementState;
  accountId: string;
  activeUserId: string;
  activeOrgId: string;
  addEmailAddress: (emailAddress: string) => Promise<UserAccountPatchResponse>;
  deleteAccountAlias: () => Promise<UserAccountPatchResponse>;
  deleteEmailAddress: () => Promise<UserAccountPatchResponse>;
  addAccountAlias: (organizationId: string, accountAlias: string) => Promise<UserAccountPatchResponse>;
  emailAddress: ElementState;
  setAccountAlias: React.Dispatch<React.SetStateAction<ElementState>>;
  setEmailAddress: React.Dispatch<React.SetStateAction<ElementState>>;
  selectedAccount: IUserAccount | undefined;
  modifyEmailAddress: (emailAddress: string) => Promise<UserAccountPatchResponse>;
  modifyAccountAlias: (organizationId: string, accountAlias: string) => Promise<UserAccountPatchResponse>;
  undeleteAccountAlias: () => Promise<UserAccountPatchResponse>;
  undeleteEmailAddress: () => Promise<UserAccountPatchResponse>;
}

interface IHandleOnBlur {
  addFn: (value: string) => Promise<UserAccountPatchResponse>;
  deleteFn: () => Promise<UserAccountPatchResponse>;
  elementName: string;
  elementState: ElementState;
  errorMsg: string;
  event: React.FocusEvent<HTMLInputElement>;
  handleSetState: (value: string) => void;
  modifyFn: (value: string) => Promise<UserAccountPatchResponse>;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setElementState: React.Dispatch<React.SetStateAction<ElementState>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setVerification: React.Dispatch<React.SetStateAction<boolean>>;
  undeleteFn: () => Promise<UserAccountPatchResponse>;
  validationFn: (value: string) => boolean;
}

const isValidEmail = R.test(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i);
const isDuplicateEmail = (ua: IUserAccount) => {
  return ua.emailAddresses.elems[0].state.tag === EmailAddressTag.DuplicateAddress;
};
const wasAliasRenamed = (ua: IUserAccount) => {
  const currentAlias = getCurrentAliases(ua);
  return currentAlias?.state.tag.tag === AliasTag.GrantedAlias && currentAlias?.state.tag.content !== null;
};

export const BasicInformation = (props: BasicInformationProps) => {
  const {
    activeUserId,
    addEmailAddress,
    emailAddress,
    setEmailAddress,
    modifyEmailAddress,
    deleteEmailAddress,
    undeleteEmailAddress,
    addAccountAlias,
    modifyAccountAlias,
    deleteAccountAlias,
    undeleteAccountAlias,
    accountAlias,
    setAccountAlias,
    selectedAccount,
    activeOrgId,
    accountId,
  } = props;
  const organizationId = selectedAccount?.owner ?? activeOrgId;
  const [emailAddressError, setEmailAddressError] = React.useState<string | null>(null);
  const [aliasError, setAliasError] = React.useState<string | null>(null);
  const [accountAliasInputValue, setAccountAliasInputValue] = React.useState<string | undefined>(undefined);
  const [emailVerification, setEmailVerification] = React.useState<boolean>(false);
  const [aliasVerification, setAliasVerification] = React.useState<boolean>(false);
  const [editAlias, setEditAlias] = React.useState<boolean>(false);
  const [editEmail, setEditEmail] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!accountAlias.isDeleted) {
      setAccountAliasInputValue(accountAlias.value);
    }
  }, [accountAlias.value]);

  const handleSetEmailAddress = (value: string) => {
    api.users.getUserById(activeUserId, `acc.${accountId}?`).then(u => {
      if (isDuplicateEmail(u)) {
        setEmailAddressError("This e-mail address is already associated to another account.");
        setEmailAddress({ value, isDeleted: false });
        return;
      }
      setEmailAddress({ value, isDeleted: false });
      setEmailVerification(true);
      setEmailAddressError(null);
    });
  };

  const handleSetAccountAlias = (value: string) => {
    api.users.getUserById(activeUserId, `acc.${accountId}?`).then(u => {
      if (wasAliasRenamed(u)) {
        const currentAlias = getCurrentAliases(u);
        const newAlias = currentAlias?.state.alias.name;
        setAliasError(
          `Sorry, the username '${value}' was either already taken or it contains invalid characters. We have therefore assigned you the username '${newAlias}'.`
        );
        setAliasVerification(true);
        setAccountAlias({ value: newAlias, isDeleted: false });
        setAccountAliasInputValue(newAlias);
      } else {
        setAliasError(null);
        setAliasVerification(true);
        setAccountAlias({ value, isDeleted: false });
        setAccountAliasInputValue(value);
      }
    });
  };

  const handleOnBlur = (args: IHandleOnBlur) => {
    const {
      addFn,
      deleteFn,
      elementName,
      elementState,
      errorMsg,
      event,
      handleSetState,
      modifyFn,
      undeleteFn,
      setEdit,
      setElementState,
      setError,
      setVerification,
      validationFn,
    } = args;
    const { value } = event.target;
    setEdit(false);
    setVerification(false);
    setError(null);
    if (value === "") {
      if (!elementState.isDeleted && elementState.value) {
        deleteFn().then(([err, res]) => {
          if (err || !res) {
            setError(`Error deleting ${elementName}`);
          }
          setElementState({ value: elementState.value, isDeleted: true });
        });
      }
      return;
    }
    if (!validationFn(value)) {
      setError(errorMsg);
      return;
    }

    if (!elementState.value) {
      addFn(value).then(([err, res]) => {
        if (err || !res) {
          setError(`Error adding ${elementName}`);
        }
        handleSetState(value);
      });
      return;
    }

    if (elementState.value === value && elementState.isDeleted) {
      undeleteFn().then(([err, res]) => {
        if (err || !res) {
          setError(`Error undeleting ${elementName}`);
        }
        setElementState({ ...elementState, isDeleted: false });
        setAccountAliasInputValue(value);
      });
      return;
    }

    if (elementState.value !== value) {
      if (elementState.isDeleted) {
        undeleteFn().then(([err, res]) => {
          if (err || !res) {
            setError(`Error undeleting ${elementName}`);
          }
          setElementState({ ...elementState, isDeleted: false });
          modifyFn(value).then(([e, r]) => {
            if (e || !r) {
              setError(`Error modifying ${elementName}`);
            }
            handleSetState(value);
          });
        });
        return;
      }
      modifyFn(value).then(([err, res]) => {
        if (err || !res) {
          setError(`Error modifying ${elementName}`);
        }
        handleSetState(value);
      });
    }
  };

  const handleOnBlurEmail = (event: React.FocusEvent<HTMLInputElement>) => {
    handleOnBlur({
      addFn: addEmailAddress,
      deleteFn: deleteEmailAddress,
      elementName: "e-mail address",
      elementState: emailAddress,
      errorMsg: "This e-mail address is invalid.",
      event,
      handleSetState: handleSetEmailAddress,
      modifyFn: modifyEmailAddress,
      setEdit: setEditEmail,
      setElementState: setEmailAddress,
      setError: setEmailAddressError,
      setVerification: setEmailVerification,
      undeleteFn: undeleteEmailAddress,
      validationFn: isValidEmail,
    });
  };

  const handleOnBlurAlias = (event: React.FocusEvent<HTMLInputElement>) => {
    handleOnBlur({
      addFn: R.partial(addAccountAlias, [organizationId]),
      deleteFn: deleteAccountAlias,
      elementName: "account alias",
      elementState: accountAlias,
      errorMsg: "Please enter a valid account alias.",
      event,
      handleSetState: handleSetAccountAlias,
      modifyFn: R.partial(modifyAccountAlias, [organizationId]),
      setEdit: setEditAlias,
      setElementState: setAccountAlias,
      setError: setAliasError,
      setVerification: setAliasVerification,
      undeleteFn: undeleteAccountAlias,
      validationFn: () => true,
    });
  };

  const handleOnClickAlias = () => setEditAlias(true);
  const handleOnClickEmail = () => setEditEmail(true);
  const handleOnChangeAlias = (e: ChangeEvent<HTMLInputElement>) => setAccountAliasInputValue(e.target.value);
  const closeOpenText = (fn: React.Dispatch<React.SetStateAction<string | null>>) => () => fn(null);

  return (
    <>
      <AccountFormsField
        edit={editEmail}
        legend="E-mail address"
        type="email"
        name="email"
        aria-label="email"
        onBlur={handleOnBlurEmail}
        defaultValue={emailAddress.isDeleted ? undefined : emailAddress.value}
        errorMessage={emailAddressError}
        closeOpenText={closeOpenText(setEmailAddressError)}
        displayVerification={emailVerification}
        onClickInput={handleOnClickEmail}
      />
      <AccountFormsField
        edit={editAlias}
        legend="Username"
        type="text"
        id="username"
        name="username"
        aria-label="username"
        onBlur={handleOnBlurAlias}
        value={accountAliasInputValue}
        onChange={handleOnChangeAlias}
        errorMessage={aliasError}
        closeOpenText={closeOpenText(setAliasError)}
        displayVerification={aliasVerification}
        onClickInput={handleOnClickAlias}
      />
    </>
  );
};
